import { useMemo, useState } from 'react';
import { Hint } from '@/components/Hint';
import { cn } from '@/lib/utils';
import { StatusPill } from '@/components/Status';
import { statusInk } from '@/lib/status';
import { buildThreeWay, type OrderDetail } from '@/api/orders';
import { useAuth } from '@/auth/useAuth';
import { ProductIdentity } from '../ProductLink';
import { unitCost } from '../lib';
import { WorkSegmented } from '../workLib';

const VARIANCE_LABELS: Record<string, string> = {
  short: 'short',
  over: 'over',
  not_received: 'not received',
  not_invoiced: 'not invoiced',
  not_ordered: 'not on order',
  skipped: 'announced, not ordered',
};

/** One row per product across the four documents. */
export function ItemsTab({ detail }: { detail: OrderDetail }) {
  // The server nulls every cost without orders:read:costs; the two cost
  // columns then say nothing, so they are not drawn at all.
  const showCosts = useAuth().can('orders:read:costs');
  const threeWay = useMemo(() => buildThreeWay(detail), [detail]);
  const varianceCount = useMemo(() => threeWay.filter((r) => r.variance).length, [threeWay]);
  // A big journey opens on its exceptions: 700 clean rows bury the 30 that disagree.
  const [scope, setScope] = useState<'all' | 'variances'>(varianceCount > 20 ? 'variances' : 'all');
  const rows = scope === 'variances' ? threeWay.filter((r) => r.variance) : threeWay;
  const totals = useMemo(() => ({
    ordered: threeWay.reduce((a, r) => a + (r.qtyOrdered ?? 0), 0),
    invoiced: threeWay.reduce((a, r) => a + (r.qtyInvoiced ?? 0), 0),
    received: threeWay.reduce((a, r) => a + (r.qtyReceived ?? 0), 0),
  }), [threeWay]);

  if (threeWay.length === 0) {
    return <p className="flex flex-col items-center gap-3 px-6 py-12 text-center text-[14px] text-muted-foreground">No itemised documents captured yet.</p>;
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <WorkSegmented<'all' | 'variances'>
          label="Items"
          value={scope}
          onChange={setScope}
          options={[
            { value: 'all', label: `All ${threeWay.length}` },
            { value: 'variances', label: `Variances ${varianceCount}` },
          ]}
        />
      </div>
      <div className="overflow-x-auto rounded-md border border-border/60">
        <table
          className={cn(
            'w-full text-xs',
            // A variance row carries the WorkTable warning tone: wash on every cell, edge on the first.
            '[&_tr[data-tone=warning]>td]:bg-warning-surface/45 [&_tr[data-tone=warning]>td:first-child]:shadow-[inset_3px_0_0_var(--tone-warning)]',
          )}
        >
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left font-normal">Item</th>
              <th className="px-2 py-1.5 text-right font-normal"><Hint content="What we asked for.">Ordered</Hint></th>
              <th className="px-2 py-1.5 text-right font-normal"><Hint content="What they said they sent.">Invoiced</Hint></th>
              <th className="px-2 py-1.5 text-right font-normal"><Hint content="What we actually got.">Received</Hint></th>
              <th className="px-2 py-1.5 text-left font-normal"> </th>
              {showCosts && (
                <>
                  <th className="px-2 py-1.5 text-right font-normal"><Hint content="Invoice unit cost, as printed.">Inv cost</Hint></th>
                  <th className="px-2 py-1.5 text-right font-normal">
                    <Hint content="Cost booked on the receiving in acot-db. It matches the invoice cost only once freight and fees are distributed and the receiving is balanced.">Booked</Hint>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-border/40" data-tone={row.variance ? 'warning' : undefined}>
                <td className="max-w-[26rem] px-2 py-1">
                  <div className="line-clamp-2">
                    {row.desc}
                    {row.resolution === 'new_product' && (
                      <StatusPill tone="warning" className="ml-1.5">not set up</StatusPill>
                    )}
                  </div>
                  <ProductIdentity pid={row.pid} itemnumber={row.itemnumber} vendorSku={row.vendorSku} className="block text-xs text-muted-foreground" />
                </td>
                <td className="px-2 py-1 text-right align-top tabular-nums">
                  {row.qtyOrdered ?? '·'}
                  {row.qtyRefunded ? (
                    <Hint content="Refunded or removed from the order." underline={false}>
                      <span className="cursor-help text-muted-foreground"> −{row.qtyRefunded}</span>
                    </Hint>
                  ) : null}
                </td>
                <td className="px-2 py-1 text-right align-top tabular-nums">{row.qtyInvoiced ?? '·'}</td>
                <td className={cn('px-2 py-1 text-right align-top tabular-nums', row.variance && row.variance !== 'not_ordered' && row.variance !== 'skipped' && cn('font-medium', statusInk('warning')))}>
                  {row.qtyReceived ?? '·'}
                </td>
                <td className="whitespace-nowrap px-2 py-1 align-top text-xs">
                  {row.variance && <span className={statusInk('warning')}>{VARIANCE_LABELS[row.variance] || row.variance}</span>}
                </td>
                {showCosts && (
                  <>
                    <td className="px-2 py-1 text-right align-top tabular-nums">{unitCost(row.unitCost)}</td>
                    <td className={cn(
                      'px-2 py-1 text-right align-top tabular-nums',
                      row.bookedCostEach != null && row.unitCost != null && Math.abs(row.bookedCostEach - row.unitCost) > 0.005 && 'text-muted-foreground'
                    )}>
                      {unitCost(row.bookedCostEach)}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border/60 bg-muted/30 font-medium">
              <td className="px-2 py-1">Totals</td>
              <td className="px-2 py-1 text-right tabular-nums">{totals.ordered || '·'}</td>
              <td className="px-2 py-1 text-right tabular-nums">{totals.invoiced || '·'}</td>
              <td className="px-2 py-1 text-right tabular-nums">{totals.received || '·'}</td>
              <td colSpan={showCosts ? 3 : 1} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
